/**
 * The worlds the hero stage can be switched between.
 *
 * A world is everything that changes at once when a card is picked: the three
 * stops of the stage gradient, the glyphs the orbit carries, the accents the
 * phone screen paints with, and the screen itself. They live here rather than
 * beside either page because the two sets have to stay recognisably the same
 * kind of thing — the moment one grows a field the other doesn't have, the
 * stage has to start guessing.
 *
 * `key` is what `onWorldChange` hands back, so on the services page it has to
 * match a catalogue group exactly.
 */

/* ------------------------------------------------------------------- home */

export const HOME_WORLDS = [
  {
    key: "shop",
    label: "Shop",
    tagline: "Fashion, tech & home",
    glyph: "market",
    theme: { inner: "#6A0DAD", mid: "#3B0764", outer: "#12021D" },
    accent: {
      tile: "linear-gradient(140deg,#F4E8FF,#EADCFB)",
      promo: "linear-gradient(140deg,#7B1BC4,#4C087D)"
    },
    orbIcons: [
      "fashion",
      "electronics",
      "home",
      "gifts",
      "market",
      "bazaar",
      "cars",
      "factories",
      "sparkle"
    ],
    screen: {
      searchHint: "Search nine categories",
      location: "Amman, Jordan",
      heading: "Categories",
      tiles: [
        { icon: "fashion", label: "Fashion" },
        { icon: "electronics", label: "Tech" },
        { icon: "home", label: "Home" },
        { icon: "gifts", label: "Gifts" },
        { icon: "cars", label: "Cars" },
        { icon: "realEstate", label: "Property" }
      ],
      promoTitle: "One cart, every store",
      promoSub: "Verified sellers, a single checkout, one place to track it all.",
      promoCta: "Start shopping",
      listHeading: "Trending near you",
      rows: [
        { icon: "electronics", title: "iPhone 16 Pro", meta: "Electronics · Amman", tag: "850 JOD" },
        { icon: "fashion", title: "Autumn drop", meta: "Fashion · 40 stores", tag: "New" },
        { icon: "home", title: "Studio desk set", meta: "Home · Free delivery", tag: "119 JOD" }
      ]
    }
  },
  {
    key: "travel",
    label: "Travel",
    tagline: "Hotels, flights & cars",
    glyph: "tourism",
    theme: { inner: "#5B21B6", mid: "#2E1065", outer: "#0B0518" },
    accent: {
      tile: "linear-gradient(140deg,#EDE9FE,#DDD6FE)",
      promo: "linear-gradient(140deg,#6D28D9,#3B1178)"
    },
    orbIcons: ["tourism", "send", "map", "cars", "pin", "clock", "users", "rocket", "target"],
    screen: {
      searchHint: "Where are you going?",
      location: "Queen Alia Intl · AMM",
      heading: "Book a trip",
      tiles: [
        { icon: "tourism", label: "Hotels" },
        { icon: "send", label: "Flights" },
        { icon: "cars", label: "Cars" },
        { icon: "map", label: "Packages" },
        { icon: "home", label: "Chalets" },
        { icon: "users", label: "Groups" }
      ],
      promoTitle: "2M hotels, 460 airlines",
      promoSub: "Flights, stays and transfers booked on one itinerary.",
      promoCta: "Plan a trip",
      listHeading: "Popular right now",
      rows: [
        { icon: "send", title: "Amman → Dubai", meta: "Direct · 3h 05m", tag: "189 JOD" },
        { icon: "tourism", title: "Aqaba beach resort", meta: "4.8 ★ · 2 nights", tag: "-25%" },
        { icon: "cars", title: "Airport pickup", meta: "Sedan · 4 seats", tag: "22 JOD" }
      ]
    }
  },
  {
    key: "everyday",
    label: "Everyday",
    tagline: "Food, health & more",
    glyph: "restaurants",
    theme: { inner: "#9333EA", mid: "#4C1D95", outer: "#14041F" },
    accent: {
      tile: "linear-gradient(140deg,#F5E9FF,#E9D5FF)",
      promo: "linear-gradient(140deg,#9333EA,#5B21B6)"
    },
    orbIcons: [
      "restaurants",
      "healthcare",
      "cinema",
      "chat",
      "shield",
      "bolt",
      "phone",
      "checkCircle",
      "clock"
    ],
    screen: {
      searchHint: "Dinner, a doctor, a ticket…",
      location: "Delivering to Abdoun",
      heading: "Tonight",
      tiles: [
        { icon: "restaurants", label: "Food" },
        { icon: "healthcare", label: "Health" },
        { icon: "cinema", label: "Cinema" },
        { icon: "market", label: "Grocery" },
        { icon: "gifts", label: "Gifts" },
        { icon: "bolt", label: "Express" }
      ],
      promoTitle: "Dinner in 30 minutes",
      promoSub: "The kitchens near you, live order tracking, no second app.",
      promoCta: "Order now",
      listHeading: "Ready when you are",
      rows: [
        { icon: "restaurants", title: "Shawarma house", meta: "4.9 ★ · 25 min", tag: "Free" },
        { icon: "healthcare", title: "Dr. Rania Haddad", meta: "Dermatology · Today", tag: "6pm" },
        { icon: "cinema", title: "Taj Cinemas", meta: "Two seats · Row F", tag: "9:40" }
      ]
    }
  }
];

/* ------------------------------------------------------------------ about */

/**
 * The about page has no catalogue to switch, so its three worlds are the three
 * chapters the page itself is built from — story, values, journey. Picking one
 * previews the section the visitor is about to scroll into, which is why the
 * keys match the section ids in `AboutUs.jsx`.
 *
 * The three lighting setups are deliberately the same as the home page's: this
 * is the same company seen from a different angle, not a different brand.
 */

export const ABOUT_WORLDS = [
  {
    key: "story",
    label: "Story",
    tagline: "Since 2017",
    glyph: "bulb",
    theme: { inner: "#6A0DAD", mid: "#3B0764", outer: "#12021D" },
    accent: {
      tile: "linear-gradient(140deg,#F4E8FF,#EADCFB)",
      promo: "linear-gradient(140deg,#7B1BC4,#4C087D)"
    },
    orbIcons: [
      "bulb",
      "rocket",
      "users",
      "sparkle",
      "target",
      "chat",
      "map",
      "clock",
      "check"
    ],
    screen: {
      searchHint: "How it started",
      location: "Amman, Jordan",
      heading: "Our story",
      tiles: [
        { icon: "bulb", label: "Idea" },
        { icon: "users", label: "Team" },
        { icon: "rocket", label: "Launch" },
        { icon: "map", label: "Region" },
        { icon: "sparkle", label: "Growth" },
        { icon: "target", label: "Today" }
      ],
      promoTitle: "One app, one country, then more",
      promoSub:
        "A small team in Amman set out to put everything daily life needs behind a single login.",
      promoCta: "Read our story",
      listHeading: "The short version",
      rows: [
        { icon: "bulb", title: "Founded in Amman", meta: "A team of builders", tag: "2017" },
        { icon: "map", title: "Across the region", meta: "Middle East clients", tag: "2021" },
        { icon: "sparkle", title: "Nine categories", meta: "One account", tag: "Now" }
      ]
    }
  },
  {
    key: "values",
    label: "Values",
    tagline: "What we stand for",
    glyph: "target",
    theme: { inner: "#5B21B6", mid: "#2E1065", outer: "#0B0518" },
    accent: {
      tile: "linear-gradient(140deg,#EDE9FE,#DDD6FE)",
      promo: "linear-gradient(140deg,#6D28D9,#3B1178)"
    },
    orbIcons: [
      "target",
      "shield",
      "users",
      "checkCircle",
      "bolt",
      "sparkle",
      "bulb",
      "chat",
      "rocket"
    ],
    screen: {
      searchHint: "What guides the work",
      location: "Every project, every day",
      heading: "Our values",
      tiles: [
        { icon: "rocket", label: "Innovation" },
        { icon: "shield", label: "Trust" },
        { icon: "target", label: "Excellence" },
        { icon: "sparkle", label: "Growth" },
        { icon: "users", label: "Impact" },
        { icon: "bolt", label: "Speed" }
      ],
      promoTitle: "Verified before it ships",
      promoSub:
        "Every business on Mondoway is checked, so every order and booking is accountable.",
      promoCta: "See our values",
      listHeading: "How we work",
      rows: [
        { icon: "shield", title: "Verified partners", meta: "Checked before listing", tag: "Always" },
        { icon: "users", title: "Built with clients", meta: "Partners, not tickets", tag: "Open" },
        { icon: "target", title: "Finished properly", meta: "Quality over volume", tag: "Done" }
      ]
    }
  },
  {
    key: "journey",
    label: "Journey",
    tagline: "Milestones so far",
    glyph: "rocket",
    theme: { inner: "#9333EA", mid: "#4C1D95", outer: "#14041F" },
    accent: {
      tile: "linear-gradient(140deg,#F5E9FF,#E9D5FF)",
      promo: "linear-gradient(140deg,#9333EA,#5B21B6)"
    },
    orbIcons: [
      "rocket",
      "clock",
      "checkCircle",
      "map",
      "pin",
      "users",
      "sparkle",
      "target",
      "bolt"
    ],
    screen: {
      searchHint: "Eight years, so far",
      location: "2017 → today",
      heading: "Our journey",
      tiles: [
        { icon: "bulb", label: "2017" },
        { icon: "checkCircle", label: "2019" },
        { icon: "map", label: "2021" },
        { icon: "sparkle", label: "2023" },
        { icon: "rocket", label: "2025" },
        { icon: "target", label: "Next" }
      ],
      promoTitle: "150+ projects delivered",
      promoSub:
        "Eight years of shipping, from a first handful of clients to a platform serving the region.",
      promoCta: "See the timeline",
      listHeading: "Milestones",
      rows: [
        { icon: "checkCircle", title: "50+ projects", meta: "First major milestone", tag: "2019" },
        { icon: "map", title: "Middle East", meta: "International expansion", tag: "2021" },
        { icon: "sparkle", title: "AI integration", meta: "Smarter for clients", tag: "2023" }
      ]
    }
  }
];

/* --------------------------------------------------------------- services */

/**
 * One world per catalogue group. `key` is the group name verbatim — the
 * services page filters on what `onWorldChange` returns, so a typo here shows
 * up as a card that silently empties the grid.
 */
export const SERVICE_WORLDS = [
  {
    key: "Shopping",
    label: "Shopping",
    tagline: "Six ways to buy",
    glyph: "market",
    theme: { inner: "#6A0DAD", mid: "#3B0764", outer: "#12021D" },
    accent: {
      tile: "linear-gradient(140deg,#F4E8FF,#EADCFB)",
      promo: "linear-gradient(140deg,#7B1BC4,#4C087D)"
    },
    orbIcons: [
      "fashion",
      "gifts",
      "electronics",
      "home",
      "market",
      "bazaar",
      "check",
      "sparkle",
      "shield"
    ],
    screen: {
      searchHint: "Search stores and products",
      location: "Amman, Jordan",
      heading: "Shopping",
      tiles: [
        { icon: "fashion", label: "Clothes" },
        { icon: "gifts", label: "Gifts" },
        { icon: "electronics", label: "Tech" },
        { icon: "home", label: "Home" },
        { icon: "market", label: "Market" },
        { icon: "bazaar", label: "Bazaar" }
      ],
      promoTitle: "Six shopping services",
      promoSub: "From a new phone to weekly groceries, on one account.",
      promoCta: "Browse stores",
      listHeading: "Verified sellers",
      rows: [
        { icon: "electronics", title: "Latest tech", meta: "Brand warranty", tag: "New" },
        { icon: "market", title: "Mart Market", meta: "Same day delivery", tag: "Fast" },
        { icon: "bazaar", title: "Bazaar", meta: "Buy & sell used", tag: "Rated" }
      ]
    }
  },
  {
    key: "Leisure & Travel",
    label: "Leisure",
    tagline: "Eat, watch, travel",
    glyph: "tourism",
    theme: { inner: "#5B21B6", mid: "#2E1065", outer: "#0B0518" },
    accent: {
      tile: "linear-gradient(140deg,#EDE9FE,#DDD6FE)",
      promo: "linear-gradient(140deg,#6D28D9,#3B1178)"
    },
    orbIcons: [
      "tourism",
      "restaurants",
      "cinema",
      "send",
      "map",
      "pin",
      "clock",
      "users",
      "sparkle"
    ],
    screen: {
      searchHint: "Where are you going?",
      location: "Queen Alia Intl · AMM",
      heading: "Leisure & Travel",
      tiles: [
        { icon: "tourism", label: "Tourism" },
        { icon: "restaurants", label: "Dining" },
        { icon: "cinema", label: "Cinema" },
        { icon: "send", label: "Flights" },
        { icon: "map", label: "Tours" },
        { icon: "users", label: "Groups" }
      ],
      promoTitle: "350,000 hotels",
      promoSub: "A whole trip — stays, tours and travel agencies in one place.",
      promoCta: "Plan a trip",
      listHeading: "Book tonight",
      rows: [
        { icon: "restaurants", title: "Restaurants", meta: "Real-time tracking", tag: "25 min" },
        { icon: "cinema", title: "Cinema tickets", meta: "Pick your seats", tag: "9:40" },
        { icon: "tourism", title: "Hotels worldwide", meta: "350K+ properties", tag: "-25%" }
      ]
    }
  },
  {
    key: "Property & Auto",
    label: "Property",
    tagline: "Homes & vehicles",
    glyph: "realEstate",
    theme: { inner: "#7C3AED", mid: "#3B1178", outer: "#0F0320" },
    accent: {
      tile: "linear-gradient(140deg,#F1EAFE,#E3D7FB)",
      promo: "linear-gradient(140deg,#7C3AED,#4C1D95)"
    },
    orbIcons: [
      "realEstate",
      "cars",
      "home",
      "pin",
      "map",
      "check",
      "shield",
      "target",
      "users"
    ],
    screen: {
      searchHint: "Buy, rent or list",
      location: "Abdoun · Amman",
      heading: "Property & Auto",
      tiles: [
        { icon: "realEstate", label: "Property" },
        { icon: "cars", label: "Cars" },
        { icon: "home", label: "Villas" },
        { icon: "map", label: "Land" },
        { icon: "shield", label: "Legal" },
        { icon: "target", label: "Valuation" }
      ],
      promoTitle: "Own it or rent it",
      promoSub: "Houses, apartments, villas, farms and land — listed and verified.",
      promoCta: "See listings",
      listHeading: "On the market",
      rows: [
        { icon: "realEstate", title: "3-bed apartment", meta: "Abdoun · 180 m²", tag: "Rent" },
        { icon: "cars", title: "Test drive booking", meta: "Global brands", tag: "Free" },
        { icon: "home", title: "Dead Sea chalet", meta: "Sleeps 6 · Pool", tag: "New" }
      ]
    }
  },
  {
    key: "Services",
    label: "Services",
    tagline: "Health & industry",
    glyph: "healthcare",
    theme: { inner: "#9333EA", mid: "#4C1D95", outer: "#14041F" },
    accent: {
      tile: "linear-gradient(140deg,#F5E9FF,#E9D5FF)",
      promo: "linear-gradient(140deg,#9333EA,#5B21B6)"
    },
    orbIcons: [
      "healthcare",
      "factories",
      "shield",
      "checkCircle",
      "chat",
      "phone",
      "clock",
      "bolt",
      "users"
    ],
    screen: {
      searchHint: "Find a doctor or a team",
      location: "Amman, Jordan",
      heading: "Services",
      tiles: [
        { icon: "healthcare", label: "Clinics" },
        { icon: "factories", label: "Factories" },
        { icon: "chat", label: "Consult" },
        { icon: "shield", label: "Verified" },
        { icon: "clock", label: "Booking" },
        { icon: "users", label: "Teams" }
      ],
      promoTitle: "Care and craft, booked",
      promoSub: "The best clinics across every field, and teams for the work.",
      promoCta: "Find a provider",
      listHeading: "Available today",
      rows: [
        { icon: "healthcare", title: "Dr. Rania Haddad", meta: "Dermatology", tag: "6pm" },
        { icon: "chat", title: "Online consultation", meta: "Medical records", tag: "Live" },
        { icon: "factories", title: "Project teams", meta: "Quality assured", tag: "Quote" }
      ]
    }
  }
];
